import React, { useState, useEffect, useRef } from "react";
import socket from "src/socket";
import { useNavigate } from "react-router-dom";
import messageApi from "../../api/message";
import axiosInstance from "../../api/api";
import { FaArrowLeft, FaPaperclip, FaPaperPlane } from "react-icons/fa";
import PostEventReview from "./PostEventReview";

const OneToOneChat = ({ currentUser, otherUser, eventId }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [userData, setUserData] = useState(otherUser);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const currentId = currentUser?._id;
  const targetId = otherUser?._id;

  useEffect(() => {
    const fetchUserDetails = async () => {
      // 10 characters check is for valid mongoID string safety
      if (!targetId || targetId === "undefined" || targetId.length < 10) return;

      try {
        // Axios base URL check: /api/auth/user or /auth/user
        const res = await axiosInstance.get(`/auth/user/${targetId}`);
        if (res.data) {
          setUserData({
            _id: targetId,
            name: res.data.fullName,
            avatar: res.data.profilePicture,
          });
        }
      } catch (err) {
        console.error("Frontend User Details Fetch Error:", err);
        setUserData((prev) => ({ ...prev, name: "User" })); // Fallback on 404
      }
    };

    // Trigger fetch if name is missing or "Loading..."
    if (
      !otherUser?.name ||
      otherUser.name === "undefined" ||
      otherUser.name === "Loading..."
    ) {
      fetchUserDetails();
    } else {
      setUserData(otherUser);
    }
  }, [targetId, otherUser]);

  useEffect(() => {
    if (!currentId || !targetId) return;

    const fetchHistory = async () => {
      try {
        const res = await messageApi.getMessages(currentId, targetId);
        setMessages(res.data || []);
        await messageApi.markAsRead(targetId, currentId);
      } catch (err) {
        console.error("Load Error:", err);
      }
    };

    fetchHistory();

    const handleReceive = (data) => {
      if (data.senderId === targetId || data.senderId === currentId) {
        setMessages((prev) => [...prev, data]);
        if (data.senderId === targetId) {
          messageApi.markAsRead(targetId, currentId);
        }
      }
    };

    socket.on("receiveMessage", handleReceive);
    socket.on("typing", ({ from }) => {
      if (from === targetId) setIsTyping(true);
    });
    socket.on("stopTyping", ({ from }) => {
      if (from === targetId) setIsTyping(false);
    });

    return () => {
      socket.off("receiveMessage", handleReceive);
      socket.off("typing");
      socket.off("stopTyping");
    };
  }, [currentId, targetId]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    const shouldAutoScroll = distanceFromBottom < 140;

    if (shouldAutoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
  }, [targetId]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const msgData = {
      senderId: currentId,
      receiverId: targetId,
      message: newMessage,
    };

    socket.emit("sendMessage", msgData);
    setNewMessage("");
    socket.emit("stopTyping", { senderId: currentId, receiverId: targetId });
  };

  const onTyping = (e) => {
    setNewMessage(e.target.value);
    socket.emit("typing", { senderId: currentId, receiverId: targetId });
    if (window.typingTimeout) clearTimeout(window.typingTimeout);
    window.typingTimeout = setTimeout(() => {
      socket.emit("stopTyping", { senderId: currentId, receiverId: targetId });
    }, 2000);
  };

  const getMessageImages = (msg) => {
    const candidate =
      msg?.images ||
      msg?.image ||
      msg?.imageUrl ||
      msg?.mediaUrl ||
      msg?.media?.url;

    if (Array.isArray(candidate)) {
      return candidate.filter(
        (item) => typeof item === "string" && item.trim(),
      );
    }

    if (typeof candidate === "string" && candidate.trim()) {
      return [candidate];
    }

    return [];
  };

  const formatMessageTime = (dateValue) => {
    if (!dateValue) return "";
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="flex flex-col h-full min-h-0 bg-[#f5f5f5]">
      <div className="px-4 sm:px-5 py-3 border-b border-gray-200 flex items-center justify-between bg-white">
        <div className="flex items-center gap-3">
          <FaArrowLeft
            className="lg:hidden cursor-pointer text-gray-500"
            onClick={() => navigate(-1)}
          />

          {userData?.avatar ? (
            <img
              src={userData.avatar}
              className="w-10 h-10 rounded-full object-cover border border-orange-100"
              alt=""
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-500 font-bold border border-orange-200">
              {userData?.name ? userData.name.charAt(0).toUpperCase() : "U"}
            </div>
          )}

          <div>
            <h3 className="font-semibold text-gray-900 leading-tight text-sm sm:text-base">
              {userData?.name || "Loading..."}
            </h3>
            <p className="text-[11px] text-gray-500 font-medium flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
              {isTyping ? "typing..." : "Online"}
            </p>
          </div>
        </div>
      </div>

      <div
        ref={messagesContainerRef}
        className="flex-1 min-h-0 overflow-y-auto px-3 sm:px-5 py-4 space-y-3 bg-[#f5f5f5]"
      >
        {messages.map((msg, index) => {
          const isMe = msg.senderId === currentId;
          const images = getMessageImages(msg);
          const hasText = typeof msg.message === "string" && msg.message.trim();

          return (
            <div
              key={index}
              className={`flex ${isMe ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[82%] sm:max-w-[72%] px-3.5 py-2.5 rounded-2xl shadow-sm text-sm ${isMe ? "bg-[#FF4D12] text-white rounded-br-md" : "bg-[#ECEEF1] text-gray-800 rounded-bl-md"}`}
              >
                {images.length > 0 && (
                  <div
                    className={`grid gap-1.5 ${hasText ? "mb-2" : "mb-1"} ${images.length === 1 ? "grid-cols-1" : images.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}
                  >
                    {images.slice(0, 6).map((src, imageIndex) => (
                      <img
                        key={`${index}-img-${imageIndex}`}
                        src={src}
                        alt="message"
                        className={`w-full object-cover rounded-lg ${images.length === 1 ? "max-h-60" : "h-24 sm:h-28"}`}
                        loading="lazy"
                      />
                    ))}
                  </div>
                )}

                {hasText && (
                  <p className="whitespace-pre-wrap break-words">
                    {msg.message}
                  </p>
                )}

                <p
                  className={`text-[10px] mt-1.5 text-right ${isMe ? "text-orange-100" : "text-gray-500"}`}
                >
                  {formatMessageTime(msg.createdAt)}
                </p>
              </div>
            </div>
          );
        })}
        <PostEventReview
          eventId={eventId}
          currentUser={currentUser}
          otherUser={otherUser}
        />
        <div ref={messagesEndRef} />
      </div>

      <form
        onSubmit={handleSendMessage}
        className="sticky bottom-0 p-3 sm:p-4 border-t border-gray-200 bg-white shrink-0"
      >
        <div className="flex items-center gap-2 bg-gray-100 rounded-full px-3 sm:px-4 py-2.5 border border-gray-200">
          <button
            type="button"
            aria-label="Attach file"
            className="text-gray-400 hover:text-[#FF4D12] transition-colors"
          >
            <FaPaperclip />
          </button>

          <input
            value={newMessage}
            onChange={onTyping}
            placeholder="Type a message..."
            className="flex-1 bg-transparent outline-none text-sm text-gray-700 placeholder:text-gray-400"
          />

          <button
            type="submit"
            className="w-9 h-9 rounded-full bg-[#FF4D12] text-white flex items-center justify-center hover:bg-[#e74510] transition"
          >
            <FaPaperPlane />
          </button>
        </div>
      </form>
    </div>
  );
};

export default OneToOneChat;
